import React, { useState, useEffect } from 'react';
import { FlowLanding } from './pages/FlowLanding';
import SelectProcessType from './steps/SelectProcessType';
import SelectCoffeeBean from './steps/SelectCoffeeBean';
import { SelectPackageOptions } from './steps/SelectPackageOptions';
import { SelectLotSize } from './steps/SelectLotSize';
import { DesignSubmission } from './steps/DesignSubmission';
import OrderConfirmation from './steps/OrderConfirmation';
import { ProductPreview } from './components/ProductPreview';
import { StepIndicator } from '../../components/molecules/StepIndicator/StepIndicator';
import { FlowFooter } from '../../components/organisms/FlowFooter/FlowFooter';
import PriceDisplay from '../../components/molecules/PriceDisplay/PriceDisplay';
import { Modal } from '../../components/molecules/Modal/Modal';
import { Button } from '../../components/atoms/Button';
import { useTranslations } from '../../hooks/useTranslations';
import { useCustomerInfo } from '../../hooks/useCustomerInfo';
import { useOrderRestart } from '../../hooks/useOrderRestart';
import { useOrderFlowStore } from '../../store/orderFlowStore';
import { productDataManager } from '../../lib/productDataManager';
import styles from './OrderFlowContainer.module.scss';

export const OrderFlowContainer: React.FC = () => {
  // 0: landing, 1: step 1, 2: step 2, ...
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  // 再開ブロックモーダル表示用state
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  
  const t = useTranslations();
  const { restoreOrderProgress, clearSavedProgress, checkRestrictions } = useOrderRestart();
  const { 
    productType, 
    beanType, 
    selectedBean,
    packageType,
    sugarConcentration,
    needsInnerDesign,
    needsFrontLabel,
    needsOuterBox,
    needsOuterBoxDesign,
    needsBackLabel,
    lotSize,
    userSelections,
    setAvailableProducts,
    updateCurrentProduct,
    reset,
    availableProducts,
    currentProduct,
    totalPrice,
    basePrice,
    optionPrices,
    validateUploadedFiles,
    // 戻る処理で使用するセッター関数
    setBeanType,
    setPackageType,
    setSelectedBean,
    setLotSize,
    setNeedsInnerDesign,
    setNeedsFrontLabel,
    setNeedsOuterBox,
    setNeedsOuterBoxDesign,
    setNeedsBackLabel,
    setInnerDesignFile,
    setFrontLabelFile,
    setOuterBoxDesignFile,
    setBackLabelFile,
    resetUserSelections,
    setSugarConcentration
  } = useOrderFlowStore();

  const customerInfo = useCustomerInfo();

  // 価格内訳を生成
  const generatePriceBreakdown = () => {
    const breakdown: Array<{ label: string; value: number }> = [];
    
    // ベース商品
    if (basePrice > 0) {
      let baseLabel = '';
      if (productType === 'freeze-dried') {
        baseLabel = t.priceBreakdown_freezeDriedCoffee;
      } else if (productType === 'liquid-concentrate') {
        baseLabel = t.priceBreakdown_liquidConcentrateCoffee;
      } else if (productType === 'straight-coffee') {
        baseLabel = t.priceBreakdown_straightCoffee;
      }
      
      if (beanType === 'wako' && selectedBean) {
        const beanLabel = selectedBean === 'wako-house-blend' ? t.priceBreakdown_houseBlend : t.priceBreakdown_houseDarkBlend;
        baseLabel += ` (${beanLabel})`;
      } else if (beanType === 'my-bean') {
        baseLabel += ` (${t.priceBreakdown_customerBeans})`;
      }
      
      if (packageType) {
        const packageLabel = packageType === 'stick' ? t.priceBreakdown_stick : 
                           packageType === 'cube' ? t.priceBreakdown_cube :
                           packageType === 'spout' ? t.priceBreakdown_spout : 
                           packageType === 'pouch' ? t.priceBreakdown_pouch :
                           packageType === 'bottle' ? t.priceBreakdown_bottle : '';
        baseLabel += ` (${packageLabel})`;
      }
      
      if (lotSize) {
        baseLabel += ` - ${lotSize}${t.priceBreakdown_pieces}`;
      }
      
      breakdown.push({ label: baseLabel, value: basePrice });
    }
    
    // 砂糖濃度
    if (sugarConcentration && sugarConcentration !== 'none') {
      const sugarPrice = optionPrices[`sugar_${sugarConcentration}`] || 0;
      breakdown.push({ label: `${t.priceBreakdown_sugarConcentration} ${sugarConcentration}%`, value: sugarPrice });
    }
    
    // インナーデザイン
    if (needsInnerDesign) {
      const designType = packageType === 'stick' ? t.priceBreakdown_innerDesignStick : t.priceBreakdown_innerFrontLabelCube;
      const innerPrice = optionPrices['inner_design'] || 0;
      breakdown.push({ label: designType, value: innerPrice });
    }
    
    // 表ラベル
    if (needsFrontLabel) {
      const frontPrice = optionPrices['front_label'] || 0;
      breakdown.push({ label: t.priceBreakdown_frontLabelDesign, value: frontPrice });
    }
    
    // 外箱
    if (needsOuterBox) {
      const outerLabel = needsOuterBoxDesign ? t.priceBreakdown_outerBoxWithDesign : t.priceBreakdown_outerBoxPlain;
      const outerPrice = optionPrices['outer_box'] || 0;
      breakdown.push({ label: outerLabel, value: outerPrice });
    }
    
    // 裏ラベル
    if (needsBackLabel) {
      const backPrice = optionPrices['back_label'] || 0;
      breakdown.push({ label: t.priceBreakdown_backLabelDesign, value: backPrice });
    }
    
    return breakdown;
  };

  // 価格表示が必要なステップかどうかを判定
  const shouldShowPrice = () => {
    // STEP4以降で、ロット数が選択されていて、合計価格が0より大きい場合
    return currentStep >= 4 && lotSize && totalPrice > 0;
  };

  // データ初期化
  useEffect(() => {
    const initializeData = async () => {
      try {
        setIsLoading(true);
        setInitError(null);
        
        // 商品データマネージャーを初期化
        await productDataManager.initialize();
        
        // 利用可能な商品をストアに設定
        const products = productDataManager.getAvailableProducts();
        setAvailableProducts(products);
        
      } catch (error) {
        setInitError(t.orderFlow_productDataLoadError);
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [setAvailableProducts]);

  // 商品更新の処理
  useEffect(() => {
    if (!isLoading && productDataManager.getIsInitialized()) {
      updateCurrentProduct();
    }
  }, [productType, beanType, selectedBean, isLoading, updateCurrentProduct]);

  // URL パラメータでの再開処理
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldRestart = urlParams.get('restart') === 'true';
    
    if (shouldRestart && !isLoading) {
      // URLパラメータをクリア（ブラウザ履歴を汚さないため）
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // 一時的制約チェック
      const restrictionCheck = checkRestrictions();
      
      const restartOrder = async () => {
        try {
          const result = await restoreOrderProgress();
          
          if (result.success) {
            // まずStep 5に遷移
            setCurrentStep(5);
            
            // 制約に引っかかる場合はモーダルを表示
            if (restrictionCheck.blocked) {
              setShowRestrictionModal(true);
            }
          } else {
            alert(`${t.error_restoreError}: ${result.error}`);
          }
        } catch (error) {
          alert(t.error_orderRestartError);
        }
      };
      
      restartOrder();
    }
  }, [isLoading, restoreOrderProgress, checkRestrictions]);

  // ページ離脱・遷移時のリセット処理（セーブ機能は除く）
  useEffect(() => {
    const handlePageNavigation = () => {
      // セーブ機能によるページ遷移の場合はリセットしない
      const savedProgress = localStorage.getItem('wako-custom-order-progress');
      if (!savedProgress) {
        reset();
      }
    };

    // ブラウザの戻る/進むボタン対応
    const handlePopState = (event: PopStateEvent) => {
      reset();
    };

    // ページ離脱時の処理
    const handleBeforeUnload = () => {
      // セーブ機能によるページ遷移でない場合のみリセット
      const savedProgress = localStorage.getItem('wako-custom-order-progress');
      if (!savedProgress) {
        reset();
      }
    };

    // イベントリスナーを登録
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // クリーンアップ
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [reset]); // 依存関係を明示

  const STEPS = [
    {
      sp: t.stepIndicator_step1_sp,
      pc: t.stepIndicator_step1_pc,
    },
    {
      sp: t.stepIndicator_step2_sp,
      pc: t.stepIndicator_step2_pc,
    },
    {
      sp: t.stepIndicator_step3_sp,
      pc: t.stepIndicator_step3_pc,
    },
    {
      sp: t.stepIndicator_step4_sp,
      pc: t.stepIndicator_step4_pc,
    },
    {
      sp: t.stepIndicator_step5_sp,
      pc: t.stepIndicator_step5_pc,
    },
  ];

  const handleOrderStart = () => {
    if (!isLoading && !initError) {
      setCurrentStep(1);
      
      // DOM更新を待ってからスクロール
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  const handleNext = () => {
    if (isNextDisabled()) return;
    
    setCurrentStep((prev) => prev + 1);
    
    // DOM更新を待ってからスクロール（step 1→2の遷移時の問題対応）
    // requestAnimationFrameを使用してブラウザの次回描画後にスクロール実行
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  // 途中保存して次へ（STEP4専用）
  const handleSaveAndNext = () => {
    if (isNextDisabled()) return;
    
    try {
      // 現在の注文状態をlocalStorageに保存
      const orderProgress = {
        productType,
        beanType,
        packageType,
        selectedBean,
        lotSize,
        sugarConcentration,
        needsInnerDesign,
        needsFrontLabel,
        needsOuterBox,
        needsOuterBoxDesign,
        needsBackLabel,
        currentProduct: currentProduct ? {
          targetProductHandle: currentProduct.targetProductHandle,
          productInfo: currentProduct.productInfo
        } : null,
        savedAt: new Date().toISOString(),
        customerId: customerInfo.id // 顧客IDも保存して整合性チェック
      };
      
      localStorage.setItem('wako-custom-order-progress', JSON.stringify(orderProgress));
      
      // 次のステップに進む
      setCurrentStep((prev) => prev + 1);
      
      // DOM更新を待ってからスクロール
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
      
          } catch (error) {
        alert(`${t.error_saveDataError}: ${error instanceof Error ? error.message : String(error)}`);
        // エラーが発生しても次のステップには進む
        setCurrentStep((prev) => prev + 1);
        
        // DOM更新を待ってからスクロール
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }
  };

  const handleBack = () => {
    // 前のステップに戻る際に、現在のステップ以降の選択をリセット
    const currentStepNum = currentStep;
    
    // ステップ2から戻る場合：豆タイプ、パッケージタイプ、銘柄、砂糖濃度をリセット
    if (currentStepNum === 2) {
      // productTypeは保持、それ以降をリセット
      setBeanType(null);
      setPackageType(null);
      setSelectedBean(null);
      setSugarConcentration(null);
    }
    // ステップ3から戻る場合：パッケージオプションをリセット
    else if (currentStepNum === 3) {
      // 基本選択は保持、デザインオプションをリセット
      setNeedsInnerDesign(false);
      setNeedsFrontLabel(false);
      setNeedsOuterBox(false);
      setNeedsOuterBoxDesign(false);
      setNeedsBackLabel(false);
      // ユーザー選択状態もリセット
      resetUserSelections([
        'hasSelectedInnerDesign',
        'hasSelectedFrontLabel', 
        'hasSelectedOuterBox',
        'hasSelectedOuterBoxDesign',
        'hasSelectedBackLabel'
      ]);
    }
    // ステップ4から戻る場合：ロット数をリセット
    else if (currentStepNum === 4) {
      setLotSize(null);
    }
    // ステップ5から戻る場合：アップロードファイルをリセット
    else if (currentStepNum === 5) {
      setInnerDesignFile(null);
      setFrontLabelFile(null);
      setOuterBoxDesignFile(null);
      setBackLabelFile(null);
    }
    
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleOrderProceed = () => {
    // ここで実際のShopifyカート追加APIを呼び出す
    // 現在は仮の実装として、カート画面へのリダイレクトまたは次のステップに進む
    
    // 注文完了時に保存データを削除
    clearSavedProgress();
    
    // 例: window.location.href = '/cart';
    alert(t.success_orderAdded);
  };

  const handleRestart = () => {
    // 保存データを削除
    clearSavedProgress();
    
    // 注文フローをリセットしてランディングページに戻る
    reset();
    setCurrentStep(0);
    
  };

  const handleRestartOrder = async () => {
    try {
      const result = await restoreOrderProgress();
      
      if (result.success) {
        setCurrentStep(5);
      } else {
        alert(`${t.error_restoreError}: ${result.error}`);
      }
    } catch (error) {
      alert(t.error_orderRestartError);
    }
  };

  // 再開ブロックモーダル: 新規で始める
  const handleRestrictionModalStartNew = () => {
    clearSavedProgress();
    setShowRestrictionModal(false);
    handleOrderStart();
  };

  const renderStep = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingSpinner}>
          <p>商品データを読み込み中...</p>
        </div>
      );
    }

    if (initError) {
      return (
        <div className={styles.errorContainer}>
          <p>{initError}</p>
          <button onClick={() => window.location.reload()}>
            {t.action_retry}
          </button>
        </div>
      );
    }

    // === 一時的な顧客情報表示 ===
    
    // Step1-3, 5では通常のステップコンテンツのみを返す（ProductPreviewは外側で表示）
    switch (currentStep) {
      case 1:
        return (
          <>
            <SelectProcessType />
          </>
        );
      case 2:
        return (
          <>
            <SelectCoffeeBean />
          </>
        );
      case 3:
        return (
          <>
            <SelectPackageOptions />
          </>
        );
      case 4:
        return (
          <>
            <SelectLotSize />
          </>
        );
      case 5:
        return (
          <>
            <DesignSubmission />
          </>
        );
      case 6:
        return (
          <>
            <OrderConfirmation 
              onOrderProceed={handleOrderProceed}
              onRestart={handleRestart}
            />
          </>
        );
      default:
        return (
          <>
            <FlowLanding onOrderStart={handleOrderStart} />
          </>
        );
    }
  };

  const isNextDisabled = () => {
    if (isLoading || initError) return true;
    
    switch (currentStep) {
      case 1:
        return !productType;
      case 2:
        // フリーズドライの場合：豆タイプとパッケージタイプが必要
        if (productType === 'freeze-dried') {
          if (!beanType || !packageType) return true;
          if (beanType === 'wako' && !selectedBean) return true;
        }
        // 濃縮液・ストレートの場合：豆タイプ、パッケージタイプ、砂糖濃度が必要
        else if (productType === 'liquid-concentrate' || productType === 'straight-coffee') {
          if (!beanType || !packageType || sugarConcentration === null) return true;
          if (beanType === 'wako' && !selectedBean) return true;
        }
        else {
          if (beanType === 'wako') {
            return !selectedBean;
          }
          return !beanType;
        }
        return false;
      case 3:
        // STEP3のバリデーション：各フローで必要な選択項目がすべて完了しているかチェック
        if (!productType) return true;

        // フリーズドライの場合
        if (productType === 'freeze-dried') {
          // スティックの場合：インナーデザインの選択が必要
          if (packageType === 'stick') {
            if (!userSelections.hasSelectedInnerDesign) return true;
          }
          // キューブの場合：インナー表ラベルの選択が必要
          if (packageType === 'cube') {
            if (!userSelections.hasSelectedFrontLabel) return true;
          }
        }
        
        // 濃縮液・ストレートの場合：表ラベルの選択が必要
        if (productType === 'liquid-concentrate' || productType === 'straight-coffee') {
          if (!userSelections.hasSelectedFrontLabel) return true;
        }
        
        // 全フロー共通：外箱の選択が必要
        if (!userSelections.hasSelectedOuterBox) return true;
        
        // 外箱が必要な場合：外箱デザインの選択が必要
        if (needsOuterBox && !userSelections.hasSelectedOuterBoxDesign) return true;
        
        return false;
      case 4:
        // STEP4のバリデーション：ロット数が選択されているかチェック
        return !lotSize;
      case 5:
        // STEP5のバリデーション：必要なデザインファイルがアップロードされているかチェック
        const validation = validateUploadedFiles();
        return !validation.isValid;
      case 6:
        // STEP6は最終画面なので次へボタンは無効
        return true;
      default:
        return true;
    }
  };

  // ランディングページでは全画面表示
  if (currentStep === 0) {
    return <FlowLanding onOrderStart={handleOrderStart} />;
  }

  // STEP6（注文結果画面）は特別なレイアウト（フルスクリーン）
  if (currentStep === 6) {
    return renderStep();
  }

  // STEP4は1列レイアウト
  if (currentStep === 4) {
    return (
      <div className={styles.orderFlowContainer}>
        <StepIndicator steps={STEPS} currentStep={currentStep} />
        <div className={styles.stepContentWrapper}>
          <div className={styles.singleColumnLayout}>
            <div className={styles.stepContent}>{renderStep()}</div>
            {!customerInfo.canOrder && (
              <div className={styles.approvalPendingMessage}>
                <p style={{ whiteSpace: 'pre-line' }}>
                  {t.customerInfo_approvalPending}
                  <a className={styles.approvalPendingLink} href="/account/register" target='_blank'>{t.customerInfo_approvalPendingLink}</a>
                </p>
              </div>
            )}
          </div>
          {/* 価格表示 */}
          {shouldShowPrice() && (
            <div className={styles.priceSection}>
              <div className={styles.priceContainer}>
                <PriceDisplay 
                  price={totalPrice} 
                  breakdown={generatePriceBreakdown()} 
                  isProvisional={false}
                />
              </div>
            </div>
          )}
          {/* 承認待ち顧客へのメッセージ */}
        </div>
        <FlowFooter
          onBack={handleBack}
          onNext={handleNext}
          onSaveAndNext={handleSaveAndNext}
          isNextVisible={false}
          isNextDisabled={isNextDisabled()}
          isSaveAndNextEnabled={true}
          nextText={t.action_next}
          saveAndNextText={t.flowFooter_saveAndNext}
        />
      </div>
    );
  }

  // Step1-3, 5では2列レイアウト（ProductPreview + ステップコンテンツ）
  return (
    <div className={styles.orderFlowContainer}>
      <StepIndicator steps={STEPS} currentStep={currentStep} />
      <div className={styles.orderFlowGridWrapper}>
        <div className={styles.orderFlowGrid}>
          <aside className={styles.previewArea}>
            <ProductPreview />
          </aside>
          <div className={styles.selectionArea}>
            <div className={styles.stepContent}>{renderStep()}</div>
          </div>
        </div>
        {/* 価格表示 */}
        {shouldShowPrice() && (
          <div className={styles.priceSection}>
            <div className={styles.priceContainer}>
              <PriceDisplay 
                price={totalPrice} 
                breakdown={generatePriceBreakdown()} 
                isProvisional={false}
              />
            </div>
          </div>
        )}
      </div>
      {/* STEP6では次へボタンを非表示 */}
      {currentStep !== 6 && (
        <FlowFooter
          onBack={handleBack}
          onNext={handleNext}
          isNextDisabled={isNextDisabled()}
        />
      )}

      {/* 再開ブロックモーダル */}
      <Modal
        isOpen={showRestrictionModal}
        onClose={handleRestrictionModalStartNew}
        title={t.restrictionModal_title || '再開できません'}
        actions={
          <Button
            variant="strong"
            size="large"
            fullWidth
            onClick={handleRestrictionModalStartNew}
          >
            {t.restrictionModal_startNew || '新規で注文を始める'}
          </Button>
        }
      >
        {t.restrictionModal_message || '前回の注文内容に、現在選択できないオプションが含まれています。\n恐れ入りますが、新規でご注文をお願いいたします。'}
      </Modal>
    </div>
  );
}; 